import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';

import useApi from './useApi';
import { ApiCommand } from '../Api';
import {
  CalendarResource,
  CalendarResourceMapper,
  CalendarResourceTokenTypeMapper,
} from '../constants/calendar.constants';
import urlConstants from '../constants/url.constants';
import { useDatadog } from '~/lib/hooks/useDatadog';

const {
  calendar: {
    getElementToken: getElementTokenUrl,
    getProxyElementToken: getProxyElementTokenUrl,
  },
} = urlConstants;

type GetElementTokenCallback = (data: ElementTokenData) => void;

type CalendarEventNotificationCallback = (data: CalendarEvent) => Promise<void>;

interface GenerateElementTokenParams {
  callback?: GetElementTokenCallback | CalendarEventNotificationCallback;
  providerExternalId?: string;
  subs?: string[];
}

interface setHostManagedAvailabilityParams {
  cronofyRule: string;
  shouldAllProvidersAvailable?: boolean;
}

const useCalendarElement = (
  resource: CalendarResource,
  setElementCallback?: () => void,
): {
  elementToken: string;
  generateElementToken: (params?: GenerateElementTokenParams) => Promise<void>;
  options: CalendarResourceConfigOptions;
  fetchingElementToken: boolean;
  refreshElement: () => void;
  setHostManagedAvailability: (
    params: setHostManagedAvailabilityParams,
  ) => void;
  setOptions: Dispatch<SetStateAction<CalendarResourceConfigOptions>>;
  updateElement: (newOptions: CalendarResourceConfigOptions) => void;
} => {
  const { logError } = useDatadog();
  const element = useRef(null);

  const [options, setOptions] = useState<CalendarResourceConfigOptions>(null);

  const {
    data: elementTokenData,
    sendRequest: getElementToken,
    loading: fetchingElementToken,
  } = useApi<ElementTokenData>();

  const elementToken = elementTokenData?.elementToken?.token ?? null;

  const generateElementToken = async ({
    callback,
    providerExternalId,
    subs,
  }: GenerateElementTokenParams = {}): Promise<void> => {
    try {
      getElementToken({
        callback: callback as GetElementTokenCallback,
        command: ApiCommand.POST,
        options: {
          origin: window.location.origin,
          providerExternalId,
          subs,
          tokenType: CalendarResourceTokenTypeMapper[resource],
        },
        url: providerExternalId ? getProxyElementTokenUrl : getElementTokenUrl,
      });
    } catch (e) {
      console.error(`Unable to retrieve element token: ${e}`);
      logError(e, { description: 'Unable to retrieve element token' });
    }
  };

  /** Reloads an element to pull in any recent changes since the element first loaded */
  const refreshElement = (): void => {
    element?.current?.refresh?.();
  };

  /**
   * Update the availability query related to managed availability for the host. If availability_rule_ids is
   * present and managed_availability is set to true, the free periods returned will be constrained by the host's
   * availability rule. If these properties are omitted, this is the same as ignoring any availability rules that
   * the host may have, thus returning all free periods in the host's calendar
   */

  const setHostManagedAvailability = ({
    cronofyRule,
    shouldAllProvidersAvailable,
  }: setHostManagedAvailabilityParams) => {
    setOptions((options: CalendarResourceConfigOptions) => {
      const participants = options?.availability_query?.participants?.map(
        (participant, pIndex) => ({
          ...participant,
          members: participant.members?.map((member, mIndex) => {
            const isHost = pIndex === 0 && mIndex === 0;

            if (!cronofyRule) {
              const {
                availability_rule_ids: _a,
                managed_availability: _m,
                ...rest
              } = member;

              return rest;
            }

            if (cronofyRule && shouldAllProvidersAvailable) {
              return {
                ...member,
                availability_rule_ids: [cronofyRule],
                managed_availability: true,
              };
            }

            if (cronofyRule && !shouldAllProvidersAvailable) {
              if (isHost) {
                return {
                  ...member,
                  availability_rule_ids: [cronofyRule],
                  managed_availability: true,
                };
              }

              const {
                availability_rule_ids: _a,
                managed_availability: _m,
                ...rest
              } = member;

              return rest;
            }

            return { ...member };
          }),
        }),
      );

      return {
        ...options,
        availability_query: {
          ...options?.availability_query,
          participants,
        },
      };
    });
  };

  /** Manually sets or updates an element */
  const updateElement = (newOptions: CalendarResourceConfigOptions): void => {
    if (!element?.current) {
      element.current = CalendarResourceMapper[resource].element(newOptions);
    } else {
      element.current.update(newOptions);
    }
    setElementCallback?.();
  };

  /** Handle initializing / updating calendar element */
  useEffect(() => {
    if (options?.target_id) {
      const newOptions = {
        ...options,
        element_token: elementToken ?? options.element_token,
      };
      if (
        (resource === CalendarResource.DATE_TIME_PICKER ||
          resource === CalendarResource.DATE_TIME_PICKER_V2 ||
          resource ===
            CalendarResource.RESCHEDULE_APPOINTMENT_DATE_TIME_PICKER) &&
        elementToken !== options.element_token
      ) {
        return;
      }
      if (!element?.current || elementToken !== options.element_token) {
        element.current = CalendarResourceMapper[resource].element(newOptions);
      } else if (elementToken) {
        if (resource === CalendarResource.CALENDAR_SYNC) {
          newOptions.callback =
            CalendarResourceMapper[resource].options.callback;
        }
        element.current.update(newOptions);
      }
      setElementCallback?.();
    }
  }, [elementToken, options, resource, setElementCallback]);

  return {
    elementToken,
    generateElementToken,
    options,
    fetchingElementToken,
    refreshElement,
    setHostManagedAvailability,
    setOptions,
    updateElement,
  };
};

export default useCalendarElement;
